import {
    AppBar,
    Badge,
    Box,
    Button,
    IconButton,
    Toolbar,
    Typography,
    useTheme,
    useMediaQuery,
    BottomNavigation,
    BottomNavigationAction,
    Paper,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';
import { isLogined } from '@/services/mallUser.service';
import { logOut } from '@/services/auth.service';
import { useUserStore } from '@/store/userStore';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useVendorStore } from '@/store/vendorStore';

function Nav() {
    const navigate = useNavigate();
    const { vendorPath } = useParams();
    const location = useLocation();
    const isNewProductPage = location.pathname.endsWith('/new');
    const { setIsOpen, setReferrer } = useLoginFormDialogStore((state) => state.action);
    const mallUserId = useUserStore((state) => state.mallUserId);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [selectedTab, setSelectedTab] = useState(isNewProductPage ? 1 : 0);
    const [bottomNav, setBottomNav] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const params = useParams();
    const { vendor } = useVendorStore();

    useEffect(() => {
        // 로그인 상태 확인
        setIsLoggedIn(isLogined());
    }, [mallUserId]);

    // URL 경로가 변경될 때마다 선택된 탭 업데이트
    useEffect(() => {
        setSelectedTab(isNewProductPage ? 1 : 0);
    }, [isNewProductPage]);

    // 상단 메뉴 탭 목록
    const menuTabs = [
        { label: '전체상품', color: '#222', path: `/${vendorPath}/all` },
        { label: '오늘의신상', color: '#FF4B6B', path: `/${vendorPath}/new` },
    ];

    const handleTabClick = (idx: number) => {
        setSelectedTab(idx);
        navigate(menuTabs[idx].path);
    };

    const handleUserIconClick = async () => {
        if (isLoggedIn) {
            try {
                await logOut();
                setIsLoggedIn(false);
                // 로그아웃 후 현재 경로 유지하면서 페이지 새로고침
                window.location.reload();
            } catch (error) {
                console.error('로그아웃 오류:', error);
            }
        } else {
            setIsOpen(true);
        }
    };

    // 모바일 네비게이션 컴포넌트
    function MobileNav() {
        return (
            <>
                <AppBar
                    position="sticky"
                    sx={{ background: '#fff', boxShadow: 'none', borderBottom: '1px solid #eee', zIndex: 1200, p: 0 }}
                >
                    <Box
                        sx={{
                            maxWidth: 2000,
                            margin: '0 auto',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: { xs: 2, sm: 2 },
                            py: 1,
                            minHeight: 56,
                        }}
                    >
                        {/* 좌측 로고 */}
                        {vendor?.id ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                                onClick={() => navigate(`/${vendorPath}/all`)}
                            >
                                {vendor.imageUrl ? (
                                    <Box
                                        component="img"
                                        src={vendor.imageUrl}
                                        alt={vendor.nickname}
                                        sx={{
                                            width: '42px',
                                            height: '42px',
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: '42px',
                                            height: '42px',
                                            borderRadius: 1,
                                            backgroundColor: '#f5f5f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <StoreIcon sx={{ color: '#999', fontSize: 16 }} />
                                    </Box>
                                )}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#333',
                                        fontWeight: 600,
                                        fontSize: 15,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '140px',
                                    }}
                                >
                                    {vendor.nickname}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    variant="h5"
                                    onClick={() => navigate(`/${vendorPath}/all`)}
                                    sx={{
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        fontSize: 28,
                                        letterSpacing: '0.05em',
                                        background: 'linear-gradient(45deg, #FF4B6B 30%, #FF8E53 90%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        fontFamily: "'Montserrat', sans-serif",
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            transition: 'transform 0.2s ease-in-out',
                                        },
                                    }}
                                >
                                    SHOP3
                                </Typography>
                            </Box>
                        )}
                        {/* 우측 아이콘 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* 유저 아이콘 */}
                            <IconButton size="small" onClick={handleUserIconClick}>
                                {isLoggedIn ? (
                                    <LogoutIcon sx={{ fontSize: 24 }} />
                                ) : (
                                    <PersonOutlineIcon sx={{ fontSize: 24 }} />
                                )}
                            </IconButton>
                            {/* <IconButton size="small">
                                <SearchIcon sx={{ fontSize: 24 }} />
                            </IconButton>
                            <IconButton size="small">
                                <FavoriteBorderIcon sx={{ fontSize: 24 }} />
                            </IconButton>
                            <IconButton size="small">
                                <Badge
                                    badgeContent={numOfProductInCart}
                                    color="error"
                                    sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: 16, height: 16 } }}
                                >
                                    <ShoppingCartIcon sx={{ fontSize: 24 }} />
                                </Badge>
                            </IconButton> */}
                        </Box>
                    </Box>
                    {/* 메뉴 탭 (가로 스크롤) */}
                    <Box sx={{ overflowX: 'auto', borderBottom: '1.5px solid #eee', bgcolor: '#fff', px: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 44, flexWrap: 'nowrap' }}>
                            {menuTabs.map((tab, idx) => (
                                <Button
                                    key={tab.label}
                                    onClick={() => handleTabClick(idx)}
                                    sx={{
                                        color: idx === selectedTab ? '#FF4B6B' : '#222',
                                        fontWeight: idx === selectedTab ? 700 : 500,
                                        fontSize: 16,
                                        borderRadius: 0,
                                        borderBottom:
                                            idx === selectedTab ? '2.5px solid #FF4B6B' : '2.5px solid transparent',
                                        minWidth: 0,
                                        px: 2.5,
                                        py: 1.2,
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {tab.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                </AppBar>
                {/* 하단 모바일 네비게이션 */}
                {/* <Paper
                    sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300, borderTop: '1px solid #eee' }}
                    elevation={3}
                >
                    <BottomNavigation
                        value={bottomNav}
                        onChange={(_e, newValue) => setBottomNav(newValue)}
                        showLabels
                        sx={{ height: 60, bgcolor: '#fff' }}
                    >
                        <BottomNavigationAction
                            label="홈"
                            icon={<HomeIcon />}
                            sx={{ minWidth: 0, color: bottomNav === 0 ? '#FF4B6B' : '#222' }}
                        />
                        <BottomNavigationAction
                            label="카테고리"
                            icon={<SearchIcon />}
                            sx={{ minWidth: 0, color: bottomNav === 1 ? '#FF4B6B' : '#222' }}
                        />
                        <BottomNavigationAction
                            label="매장"
                            icon={<StoreIcon />}
                            sx={{ minWidth: 0, color: bottomNav === 2 ? '#FF4B6B' : '#222' }}
                        />
                        <BottomNavigationAction
                            label="신상톡"
                            icon={<ChatBubbleOutlineIcon />}
                            sx={{ minWidth: 0, color: bottomNav === 3 ? '#FF4B6B' : '#222' }}
                        />
                        <BottomNavigationAction
                            label="마이페이지"
                            icon={<PersonOutlineIcon />}
                            sx={{ minWidth: 0, color: bottomNav === 4 ? '#FF4B6B' : '#222' }}
                        />
                    </BottomNavigation>
                </Paper> */}
            </>
        );
    }

    // PC 네비게이션 컴포넌트
    function PcNav() {
        return (
            <AppBar
                position="sticky"
                sx={{ background: '#fff', boxShadow: 'none', borderBottom: '1px solid #eee', zIndex: 1200, p: 0 }}
            >
                <Box
                    sx={{
                        maxWidth: 2000,
                        margin: '0 auto',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: { xs: 0.5, sm: 1, md: '40px', lg: '40px' },
                        py: 2.5,
                        minHeight: 56,
                        gap: 3,
                    }}
                >
                    {/* 좌측: 로고 + 메뉴(한 줄 그룹) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 120 }}>
                        {vendor?.id ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                                onClick={() => navigate(`/${vendorPath}/all`)}
                            >
                                {vendor.imageUrl ? (
                                    <Box
                                        component="img"
                                        src={vendor.imageUrl}
                                        alt={vendor.nickname}
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            objectFit: 'cover',
                                            borderRadius: '50%',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: 1.5,
                                            backgroundColor: '#f5f5f5',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <StoreIcon sx={{ color: '#999', fontSize: 18 }} />
                                    </Box>
                                )}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#333',
                                        fontWeight: 600,
                                        fontSize: 17,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '180px',
                                    }}
                                >
                                    {vendor.nickname}
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography
                                    variant="h5"
                                    onClick={() => navigate(`/${vendorPath}/all`)}
                                    sx={{
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        fontSize: 28,
                                        letterSpacing: '0.05em',
                                        background: 'linear-gradient(45deg, #FF4B6B 30%, #FF8E53 90%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        fontFamily: "'Montserrat', sans-serif",
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            transition: 'transform 0.2s ease-in-out',
                                        },
                                    }}
                                >
                                    SHOP3
                                </Typography>
                            </>
                        )}
                        {/* 메뉴 탭 (로고 옆에 붙음) */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                overflowX: 'visible',
                                bgcolor: '#fff',
                                px: 0,
                                minHeight: 44,
                                gap: 2,
                                ml: 6,
                            }}
                        >
                            {menuTabs.map((tab, idx) => (
                                <Button
                                    key={tab.label}
                                    onClick={() => handleTabClick(idx)}
                                    sx={{
                                        color: idx === selectedTab ? '#FF4B6B' : '#222',
                                        fontWeight: idx === selectedTab ? 700 : 500,
                                        fontSize: 16,
                                        borderRadius: 0,
                                        borderBottom:
                                            idx === selectedTab ? '2.5px solid #FF4B6B' : '2.5px solid transparent',
                                        minWidth: 0,
                                        px: 2.5,
                                        py: 1.2,
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {tab.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                    {/* 우측 아이콘 */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            minWidth: 120,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            color="inherit"
                            sx={{
                                fontWeight: 600,
                                fontSize: 15,
                                ml: 1,
                                color: '#222',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                px: 2,
                                py: 0.5,
                                '&:hover': {
                                    borderColor: '#999',
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                },
                            }}
                            onClick={handleUserIconClick}
                        >
                            {isLoggedIn ? '로그아웃' : '로그인'}
                        </Button>
                        {/* <IconButton size="small">
                            <SearchIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                        <IconButton size="small">
                            <FavoriteBorderIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                        <IconButton size="small">
                            <Badge
                                badgeContent={numOfProductInCart}
                                color="error"
                                sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', minWidth: 16, height: 16 } }}
                            >
                                <ShoppingCartIcon sx={{ fontSize: 24 }} />
                            </Badge>
                        </IconButton> */}
                    </Box>
                </Box>
            </AppBar>
        );
    }

    // PC/모바일 완전 분리 렌더링
    return isMobile ? <MobileNav /> : <PcNav />;
}

export default Nav;
